import { defineStore } from "pinia";
import axios, { type AxiosResponse } from "axios";
import { adminApi } from "@/services/api";
import { exportData } from "@/utils/exportService";

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: "super_admin" | "state_admin" | "facility_admin" | "admin";
  assigned_state?: string;
  permissions: string[];
  last_login: string;
  created_at: string;
  profile_image_url?: string;
  two_factor_enabled: boolean;
}

interface DoctorStats {
  id: string;
  full_name: string;
  name?: string; // For compatibility
  email: string;
  phone?: string;
  specialization: string;
  license_number?: string;
  status: "active" | "pending" | "suspended" | "deactivated" | "banned";
  approval_status?: "pending" | "approved" | "rejected";
  is_suspended?: boolean;
  registration_date: string;
  last_active: string;
  total_patients?: number;
  total_consultations?: number;
  total_earnings?: number;
  rating?: number;
  profile_completion?: number;
  whatsapp_verified?: boolean;
  email_verified?: boolean;
}

interface PlatformStats {
  overview: {
    total_doctors: number;
    active_doctors: number;
    pending_doctors: number;
    total_patients: number;
    total_users: number;
    total_appointments: number;
    average_appointment_duration: number;
    active_consultations: number;
    total_earnings: number;
    monthly_growth: number;
    patient_satisfaction: number;
    onboarding_stats: {
      completed: number;
      in_progress: number;
      not_started: number;
      total: number;
    };
    engagement_score: number;
  };
  charts: {
    user_growth: {
      labels: string[];
      data: number[];
    };
  };
  recent_activity: any;
  system_health: any;
}

interface SecurityEvent {
  id: string;
  event_type: string;
  user_id?: string;
  ip_address: string;
  user_agent: string;
  timestamp: string;
  details: Record<string, any>;
  risk_level: "low" | "medium" | "high" | "critical";
}

interface PaginationMetadata {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

interface AdminState {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  doctors: DoctorStats[];
  doctorPagination: PaginationMetadata | null;
  platformStats: PlatformStats | null;
  securityEvents: SecurityEvent[];
  financialStats: any;
  auditLogs: any[];
  auditLogPagination: PaginationMetadata | null;
  isLoading: boolean;
  interceptorsInitialized: boolean;
}

export const useAdminStore = defineStore("admin", {
  state: (): AdminState => ({
    admin: null,
    isAuthenticated: false,
    doctors: [],
    doctorPagination: null,
    platformStats: null,
    securityEvents: [],
    financialStats: null,
    auditLogs: [],
    auditLogPagination: null,
    isLoading: false,
    interceptorsInitialized: false,
  }),

  getters: {
    activeDoctors: (state) =>
      state.doctors.filter((d) => d.status === "active"),
    pendingDoctors: (state) =>
      state.doctors.filter((d) => d.status === "pending"),
    suspendedDoctors: (state) =>
      state.doctors.filter((d) => d.status === "suspended"),

    totalEarnings: (state) =>
      state.doctors.reduce(
        (total, doctor) => total + (doctor.total_earnings || 0),
        0,
      ),
    averageRating: (state) => {
      const ratings = state.doctors
        .filter((d) => d.rating !== undefined && d.rating > 0)
        .map((d) => d.rating as number);
      return ratings.length > 0
        ? ratings.reduce((a, b) => a + b, 0) / ratings.length
        : 0;
    },

    criticalSecurityEvents: (state) =>
      state.securityEvents.filter((e) => e.risk_level === "critical"),
    recentSecurityEvents: (state) => state.securityEvents.slice(0, 10),
  },

  actions: {
    // Authentication
    async login(credentials: {
      email: string;
      password: string;
      twoFactorCode?: string;
      rememberMe?: boolean;
    }) {
      try {
        this.isLoading = true;

        const response: AxiosResponse = await adminApi.post(
          "/admin/login",
          credentials,
        );
        const { admin, token, requires2FA } = response.data;

        if (requires2FA && !credentials.twoFactorCode) {
          return { requires2FA: true, success: false };
        }

        // Store admin data and token
        this.admin = admin;
        this.isAuthenticated = true;

        // Store token
        const storage = credentials.rememberMe ? localStorage : sessionStorage;
        storage.setItem("admin_token", token);

        return { success: true, requires2FA: false };
      } catch (error: any) {
        console.error("Admin login failed:", error);
        this.logFailedAttempt();
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async logout() {
      try {
        await adminApi.post("/admin/logout");
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        // Clear local state
        this.admin = null;
        this.isAuthenticated = false;

        // Clear tokens
        localStorage.removeItem("admin_token");
        sessionStorage.removeItem("admin_token");

        // Clear failed attempts
        localStorage.removeItem("adminFailedAttempts");
        localStorage.removeItem("adminLastAttempt");
      }
    },

    async checkAuthStatus() {
      const token =
        localStorage.getItem("admin_token") ||
        sessionStorage.getItem("admin_token");

      if (!token) {
        this.isAuthenticated = false;
        return false;
      }

      try {
        const response = await adminApi.get("/admin/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        this.admin = response.data.admin;
        this.isAuthenticated = true;
        return true;
      } catch (error) {
        this.isAuthenticated = false;
        localStorage.removeItem("admin_token");
        sessionStorage.removeItem("admin_token");
        return false;
      }
    },

    // Patient Management
    async fetchPatients(filters?: any) {
      try {
        this.isLoading = true;
        const response = await adminApi.get("/admin/patients", {
          params: filters,
        });
        return response.data.data;
      } catch (error) {
        console.error("Failed to fetch patients:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    // Doctor Management
    async fetchDoctors(filters?: {
      status?: string;
      specialization?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) {
      try {
        this.isLoading = true;
        const response = await adminApi.get("/admin/doctors", {
          params: filters,
        });
        const responseData = response.data.data || response.data;
        this.doctors = responseData.doctors || [];
        this.doctorPagination = responseData.pagination || null;
        return response.data;
      } catch (error) {
        console.error("Failed to fetch doctors:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateDoctorStatus(
      doctorId: string,
      status: string,
      reason?: string,
    ) {
      try {
        const response = await adminApi.put(
          `/admin/doctors/${doctorId}/status`,
          {
            status,
            reason,
          },
        );

        // Update local state
        const doctorIndex = this.doctors.findIndex((d) => d.id === doctorId);
        if (doctorIndex !== -1) {
          this.doctors[doctorIndex].status = status as any;
        }

        this.logSecurityEvent("doctor_status_changed", {
          doctor_id: doctorId,
          new_status: status,
          reason,
        });

        return response.data;
      } catch (error) {
        console.error("Failed to update doctor status:", error);
        throw error;
      }
    },

    async suspendDoctor(doctorId: string, duration: string, reason: string) {
      try {
        const response = await adminApi.post(
          `/admin/doctors/${doctorId}/suspend`,
          {
            duration,
            reason,
          },
        );

        const doctorIndex = this.doctors.findIndex((d) => d.id === doctorId);
        if (doctorIndex !== -1) {
          this.doctors[doctorIndex].status = "suspended";
          this.doctors[doctorIndex].is_suspended = true;
        }

        this.logSecurityEvent("doctor_suspended", {
          doctor_id: doctorId,
          duration,
          reason,
        });
        return response.data;
      } catch (error) {
        console.error("Failed to suspend doctor:", error);
        throw error;
      }
    },

    async unsuspendDoctor(doctorId: string) {
      try {
        const response = await adminApi.post(
          `/admin/doctors/${doctorId}/unsuspend`,
        );

        const doctorIndex = this.doctors.findIndex((d) => d.id === doctorId);
        if (doctorIndex !== -1) {
          this.doctors[doctorIndex].status = "active";
          this.doctors[doctorIndex].is_suspended = false;
        }

        this.logSecurityEvent("doctor_unsuspended", { doctor_id: doctorId });
        return response.data;
      } catch (error) {
        console.error("Failed to unsuspend doctor:", error);
        throw error;
      }
    },

    async approveDoctor(doctorId: string) {
      try {
        const response = await adminApi.post(
          `/admin/doctors/${doctorId}/approve`,
        );

        const doctorIndex = this.doctors.findIndex((d) => d.id === doctorId);
        if (doctorIndex !== -1) {
          this.doctors[doctorIndex].approval_status = "approved";
        }

        this.logSecurityEvent("doctor_approved", { doctor_id: doctorId });
        return response.data;
      } catch (error) {
        console.error("Failed to approve doctor:", error);
        throw error;
      }
    },

    async rejectDoctor(doctorId: string, reason: string) {
      try {
        const response = await adminApi.post(
          `/admin/doctors/${doctorId}/reject`,
          { reason },
        );

        const doctorIndex = this.doctors.findIndex((d) => d.id === doctorId);
        if (doctorIndex !== -1) {
          this.doctors[doctorIndex].approval_status = "rejected";
        }

        this.logSecurityEvent("doctor_rejected", {
          doctor_id: doctorId,
          reason,
        });
        return response.data;
      } catch (error) {
        console.error("Failed to reject doctor:", error);
        throw error;
      }
    },

    async getDoctorDetails(doctorId: string) {
      try {
        const response = await adminApi.get(`/admin/doctors/${doctorId}`);
        return response.data;
      } catch (error) {
        console.error("Failed to fetch doctor details:", error);
        throw error;
      }
    },

    // Platform Analytics
    async fetchSystemAnalytics(period = "30d") {
      try {
        const response = await adminApi.get(
          `/admin/reports/system-analytics?period=${period}`,
        );
        return response.data;
      } catch (error) {
        console.error("Failed to fetch system analytics:", error);
        throw error;
      }
    },

    async fetchPlatformStats(period = "30d") {
      try {
        const response = await adminApi.get("/admin/dashboard", {
          params: { period },
        });
        this.platformStats = response.data.data;
        return response.data;
      } catch (error) {
        console.error("Failed to fetch platform stats:", error);
        throw error;
      }
    },

    async getDoctorAnalytics(doctorId: string, period = "30d") {
      try {
        const response = await adminApi.get(
          `/admin/analytics/doctor/${doctorId}`,
          {
            params: { period },
          },
        );
        return response.data;
      } catch (error) {
        console.error("Failed to fetch doctor analytics:", error);
        throw error;
      }
    },

    async getRevenueAnalytics(period = "30d") {
      try {
        const response = await adminApi.get("/admin/analytics/revenue", {
          params: { period },
        });
        return response.data;
      } catch (error) {
        console.error("Failed to fetch revenue analytics:", error);
        throw error;
      }
    },

    // Financial Audit
    async fetchFinancialStats(
      period = "current_month",
      start_date?: string,
      end_date?: string,
    ) {
      try {
        const response = await adminApi.get("/admin/finance/stats", {
          params: { period, start_date, end_date },
        });
        this.financialStats = response.data;
        return response.data;
      } catch (error: any) {
        console.error("Failed to fetch financial stats:", error);
        throw error;
      }
    },

    async fetchAuditLogs(
      search = "",
      page = 1,
      start_date?: string,
      end_date?: string,
    ) {
      try {
        const response = await adminApi.get("/admin/finance/logs", {
          params: { search, page, start_date, end_date },
        });
        this.auditLogs = response.data.logs;
        this.auditLogPagination = {
          page: response.data.current_page,
          per_page: 20, // Backend uses 20 by default
          total: response.data.total,
          pages: response.data.pages,
        };
        return response.data;
      } catch (error: any) {
        console.error("Failed to fetch audit logs:", error);
        throw error;
      }
    },

    // Security & Audit
    async fetchSecurityEvents(filters?: {
      risk_level?: string;
      event_type?: string;
      user_id?: string;
      limit?: number;
    }) {
      try {
        const response = await adminApi.get("/admin/security/events", {
          params: filters,
        });
        this.securityEvents = response.data.events;
        return response.data;
      } catch (error) {
        console.error("Failed to fetch security events:", error);
        throw error;
      }
    },

    async logSecurityEvent(eventType: string, details: Record<string, any>) {
      try {
        await adminApi.post("/admin/security/log", {
          event_type: eventType,
          details,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Failed to log security event:", error);
      }
    },

    // System Management
    async getSystemHealth() {
      try {
        const response = await adminApi.get("/admin/system/health");
        return response.data;
      } catch (error) {
        console.error("Failed to fetch system health:", error);
        throw error;
      }
    },

    async exportAdminData(
      type: "doctors" | "patients" | "consultations" | "revenue",
      format: "csv" | "excel" | "pdf" = "csv",
    ) {
      try {
        // Fetch data from backend
        const response = await adminApi.get(`/admin/export/${type}`);
        const data = response.data.data || [];

        if (!data || data.length === 0) {
          throw new Error("No data available to export");
        }

        // Use the export service to generate the file
        const filename = `${type}_${new Date().toISOString().split("T")[0]}`;
        const title = `${type.charAt(0).toUpperCase() + type.slice(1)} Report`;

        await exportData(format, {
          filename,
          data,
          title,
        });

        this.logSecurityEvent("data_export", { type, format });
        return { success: true };
      } catch (error) {
        console.error("Failed to export data:", error);
        throw error;
      }
    },

    // Notifications
    async sendBulkNotification(data: {
      recipients: string[];
      title: string;
      message: string;
      type: "info" | "warning" | "success" | "error";
    }) {
      try {
        const response = await adminApi.post("/admin/notifications/bulk", data);

        this.logSecurityEvent("bulk_notification_sent", {
          recipient_count: data.recipients.length,
          type: data.type,
        });

        return response.data;
      } catch (error) {
        console.error("Failed to send bulk notification:", error);
        throw error;
      }
    },

    // Utility methods
    logFailedAttempt() {
      const attempts =
        parseInt(localStorage.getItem("adminFailedAttempts") || "0") + 1;
      localStorage.setItem("adminFailedAttempts", attempts.toString());
      localStorage.setItem("adminLastAttempt", Date.now().toString());
    },

    async setupAxiosInterceptors() {
      // Interceptors are now handled in @/services/api.js for the adminApi instance
      this.interceptorsInitialized = true;
    },
  },
});
